import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

type Scope = 'ORDERS' | 'CUSTOMERS' | 'MOVEMENTS' | 'PALLETS';

interface SavedFilter {
  id: string;
  scope: Scope;
  name: string;
  params: Record<string, string | number | boolean | null>;
}

/**
 * Tiny dropdown of the user's saved filters for a given scope. Caller
 * supplies the *current* params and a setter so users can save what they
 * have or restore a saved set.
 */
export function SavedFilters<P extends Record<string, string | number | boolean | null>>({
  scope,
  current,
  apply,
}: {
  scope: Scope;
  current: P;
  apply: (params: P) => void;
}) {
  const [filters, setFilters] = useState<SavedFilter[]>([]);
  const [reload, setReload] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<SavedFilter[]>(`/saved-filters?scope=${scope}`)
      .then(setFilters)
      .catch(() => setFilters([]));
  }, [scope, reload]);

  async function save() {
    const name = window.prompt(t('saved_filters.name_prompt'));
    if (!name?.trim()) return;
    setSaving(true);
    try {
      await api('/saved-filters', {
        method: 'POST',
        body: { scope, name: name.trim(), params: current },
      });
      setReload((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      await api(`/saved-filters/${id}`, { method: 'DELETE' });
      setReload((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <select
        value=""
        onChange={(e) => {
          const f = filters.find((x) => x.id === e.target.value);
          if (f) apply(f.params as P);
        }}
        aria-label={t('saved_filters.label')}
        className="rounded border border-border bg-background px-2 py-1"
      >
        <option value="">{t('saved_filters.placeholder')}</option>
        {filters.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="rounded border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
      >
        {t('saved_filters.save')}
      </button>
      {filters.length > 0 ? (
        <button
          type="button"
          onClick={() => {
            const id = window.prompt(
              t('saved_filters.delete_prompt') + '\n\n' + filters.map((f) => `${f.id} — ${f.name}`).join('\n'),
            );
            if (id) void remove(id);
          }}
          className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
          aria-label={t('common.delete')}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
